import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import CardEmpty from "Utils/Cards/CardEmpty";
import { NotificationManager } from "react-notifications";
import { SeleccionRecepcionesApi } from "../api/SeleccionRecepcionesApi";
import Paginacion from "../../../../../Utils/Components/Paginacion/Paginacion";
import LoadMask from "../../../../../Utils/LoadMask/LoadMask";
import { FiltrosTabs } from "../../../components/FiltosTabs";
import { RecepcionesTable } from "./RecepcionesTable";
import { useDebounce } from "../../../../../Utils/Hooks/useDebounce";
import { DatosTraslado } from "./DatosTraslado";
import { TrasladosApi } from "../../../api/TrasladosApi";
import {
    dateToDayEnd,
    dateToDayStart,
    getErrorFromResponse,
} from "../../../../../../../utility/utils";
import {
    filtrarRecepcionesPorMateriaPrima,
    getMateriaPrimaNombre,
} from "../helpers/filtrarRecepciones";

export const ListadoRecepciones = ({
    trasladoId,
    regresar,
    navigateToPesaje,
    modoEdicion,
    materiaPrimaId,
}) => {
    const { control, watch } = useForm({});

    const [recepciones, setRecepciones] = useState([]);
    const [paginaSeleccionada, setPaginaSeleccionada] = useState(1);
    const [cantidadResultados, setCantidadResultados] = useState(0);

    const [recepcionesSeleccionadas, setRecepcionesSeleccionadas] = useState(
        [],
    );

    const search = watch("search");
    const debouncedSearch = useDebounce(search, 500);
    const fecha_inicial = watch("fecha_inicial");
    const fecha_final = watch("fecha_final");

    const {
        getRecepcionesFinalizadas,
        asignarRecepcionesTraslado,
        syncRecepcionesTraslado,
        cargando,
    } = SeleccionRecepcionesApi();

    const GetRecepciones = async () => {
        try {
            const params = {
                page: paginaSeleccionada,
            };

            if (debouncedSearch) params.search = debouncedSearch;

            if (fecha_inicial) {
                params.fecha_inicial = dateToDayStart(fecha_inicial);
            }

            if (fecha_final) {
                params.fecha_final = dateToDayEnd(fecha_final);
            }

            if (modoEdicion) {
                params.traslado_id = trasladoId;
                if (materiaPrimaId) params.materia_prima_id = materiaPrimaId;
            }

            const response = await getRecepcionesFinalizadas(params);
            setRecepciones(response.results);
            setCantidadResultados(response.count);
        } catch (error) {
            // Si el error es por una página inválida, volvemos a la página anterior
            if (
                error &&
                error.detail === "Invalid page." &&
                paginaSeleccionada > 1
            ) {
                setPaginaSeleccionada((prev) => prev - 1);
                return;
            }

            const errorMsg = getErrorFromResponse(
                error,
                "Error al obtener recepciones disponibles",
            );
            NotificationManager.error(errorMsg, "Error", 3000);

            setRecepciones([]);
            setCantidadResultados(0);
        }
    };

    useEffect(() => {
        GetRecepciones();
    }, [
        paginaSeleccionada,
        debouncedSearch,
        fecha_inicial,
        fecha_final,
        modoEdicion,
    ]);

    const [trasladoDetalle, setTrasladoDetalle] = useState(null);
    const [nombreMateriaPrima, setNombreMateriaPrima] = useState("");

    const { getTrasladoDetalle, cargando: cargandoDetalle } = TrasladosApi();

    const GetTrasladoDetalle = async () => {
        try {
            const data = await getTrasladoDetalle(trasladoId);

            setTrasladoDetalle(data);

            // seleccionar las recepciones ya asociadas al traslado
            if (modoEdicion && materiaPrimaId) {
                if (!data.detalles || data.detalles.length === 0) {
                    setRecepcionesSeleccionadas([]);
                    return;
                }

                const seleccionesMateriaPrima =
                    filtrarRecepcionesPorMateriaPrima(
                        data.detalles,
                        materiaPrimaId,
                    );
                setRecepcionesSeleccionadas([...seleccionesMateriaPrima]);
                setNombreMateriaPrima(
                    getMateriaPrimaNombre(data.detalles, materiaPrimaId),
                );
            }
        } catch (error) {
            console.error("Error al obtener el detalle del traslado:", error);
            setTrasladoDetalle({});
        }
    };

    useEffect(() => {
        GetTrasladoDetalle();
    }, [trasladoId]);

    const AsignarRecepcionesTraslado = async () => {
        if (recepcionesSeleccionadas.length === 0) {
            NotificationManager.warning(
                "Debe seleccionar al menos una recepción para enviar el traslado a planta.",
                "Atención",
                3000,
            );
            return;
        }

        try {
            await asignarRecepcionesTraslado({
                ingresos_seleccionados: recepcionesSeleccionadas,
                traslado_id: trasladoDetalle.id,
            });

            NotificationManager.success(
                "Las recepciones se han asignado al traslado correctamente",
                "Éxito",
                3000,
            );

            if (modoEdicion) regresar();
            else navigateToPesaje();
        } catch (error) {
            const errorMsg = getErrorFromResponse(
                error,
                "No se pudieron asignar las recepciones al traslado.",
            );
            NotificationManager.error(errorMsg, "Error", 3000);
        }
    };

    const SyncRecepcionesTraslado = async () => {
        const movimientosIds = recepcionesSeleccionadas.map((mov) => mov.id);

        try {
            await syncRecepcionesTraslado({
                traslado_id: trasladoDetalle.id,
                materia_prima_id: materiaPrimaId, // se envía null si se está agregando materia prima
                movimientos_ids: movimientosIds,
            });

            NotificationManager.success(
                "Las recepciones del traslado se han sincronizado correctamente",
                "Éxito",
                3000,
            );

            regresar();
        } catch (error) {
            const errorMsg = getErrorFromResponse(
                error,
                "No se pudieron sincronizar las recepciones del traslado.",
            );
            NotificationManager.error(errorMsg, "Error", 3000);
        }
    };

    const SubmitAction =
        modoEdicion && materiaPrimaId
            ? SyncRecepcionesTraslado
            : AsignarRecepcionesTraslado;

    return (
        <div className="traslado-recepciones__tab-wrapper">
            <CardEmpty noShadow>
                <LoadMask loading={cargando || cargandoDetalle} blur>
                    <DatosTraslado
                        trasladoDetalle={trasladoDetalle}
                        recepcionesSeleccionadas={recepcionesSeleccionadas}
                    />

                    <div className="traslado-recepciones__table-header">
                        <FiltrosTabs
                            control={control}
                            onRefresh={GetRecepciones}
                            resetPage={() => setPaginaSeleccionada(1)}
                            searchPlaceholder={"Recepción, finca, producto"}
                        />

                        <div className="traslado-recepciones__btn-wrapper">
                            {/* chip para limpiar selecciones */}
                            {recepcionesSeleccionadas.length > 0 && (
                                <div className="traslado-recepciones__clear-chip">
                                    <span className="traslado-recepciones__clear-chip-text">
                                        {recepcionesSeleccionadas.length}{" "}
                                        {recepcionesSeleccionadas.length === 1
                                            ? "selección"
                                            : "selecciones"}
                                    </span>
                                    <button
                                        className="traslado-recepciones__clear-chip-button"
                                        onClick={() =>
                                            setRecepcionesSeleccionadas([])
                                        }
                                    >
                                        ✕
                                    </button>
                                </div>
                            )}
                            <button
                                className="btn btn-primary"
                                onClick={SubmitAction}
                                disabled={recepcionesSeleccionadas.length === 0}
                            >
                                {modoEdicion
                                    ? "Actualizar recepciones"
                                    : "Asignar recepciones al traslado"}
                            </button>
                        </div>
                    </div>

                    <div
                        className="d-flex align-items-center justify-content-between mb-3"
                        role="alert"
                    >
                        <h3>
                            Recepciones
                            {nombreMateriaPrima && " - " + nombreMateriaPrima}
                        </h3>

                        <button
                            className="btn btn-outline-dark"
                            onClick={regresar}
                        >
                            Regresar
                        </button>
                    </div>

                    <RecepcionesTable
                        recepciones={recepciones}
                        recepcionesSeleccionadas={recepcionesSeleccionadas}
                        setRecepcionesSeleccionadas={
                            setRecepcionesSeleccionadas
                        }
                    />
                    <Paginacion
                        cantidadResultados={cantidadResultados}
                        paginaSeleccionada={paginaSeleccionada}
                        setPaginaSeleccionada={setPaginaSeleccionada}
                    />
                </LoadMask>
            </CardEmpty>
        </div>
    );
};
